import groupBy from 'lodash/groupBy';
import keyBy from 'lodash/keyBy';
import { useCallback, useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { fetchTocNodes, Node, NodeWithoutDescription } from '../../../../../../../api/table-of-content';
import { getUserMaterial, UserMaterial } from '../../../../../../../api/user-material';
import { UserModule } from '../../../../../../my-methods/api/methodsApi';
import { flattenNodes } from '../../../../../../../utils/tocNode';
import { NodeWithMaterial } from '../components/NodeBranch';
import { sortModulesByCatalogName, sortSubModulesByNiceName } from '../util/sorting';
import UserModuleContext from '../../../../../../../contexts/UserModuleContext';
import { getSubModulesForModule, Module } from '../../../../../../../api/module';
import useThrowAsyncError from '../../../../../../../hooks/useThrowAsyncError';

export default function useModulesWithUserMaterial(selectedSubModule: Module | UserModule | null) {
  const { t } = useTranslation();
  const [modules, setModules] = useState<UserModule[]>([]);
  const [subModulesForModule, setSubModulesForModule] = useState<Record<string, (Module | UserModule)[]>>({});

  const [nodesWithMaterial, setNodesWithMaterial] = useState<NodeWithMaterial[]>([]);
  const [allNodesWithMaterialById, setAllNodesWithMaterialById] = useState<Record<string, NodeWithMaterial>>({});
  const [isLoadingTocNodes, setIsLoadingTocNodes] = useState(true);
  const { userModules, isLoading: isLoadingUserModules } = useContext(UserModuleContext);
  const handleError = useThrowAsyncError();

  useEffect(() => {
    const sortedModules = sortModulesByCatalogName(userModules);
    setModules(sortedModules);

    if (!sortedModules.length) {
      setIsLoadingTocNodes(false);
    }
  }, [userModules]);

  const fetchSubModulesForModule = useCallback(
    async (module: UserModule) => {
      try {
        const subModules = (await getSubModulesForModule(module.id)).data;
        const subModulesWithModule = [module, ...sortSubModulesByNiceName(subModules)];
        setSubModulesForModule(prev => ({ ...prev, [module.id]: subModulesWithModule }));
        return subModulesWithModule;
      } catch (e) {
        handleError(e);
      }
      return [];
    },
    [handleError],
  );

  useEffect(() => {
    function buildMaterialNodeTree(
      toc: NodeWithoutDescription[],
      materialByNode: Record<string, UserMaterial[]>,
    ): NodeWithMaterial[] {
      function transformNode(node: NodeWithoutDescription, pathStack: string[]): NodeWithMaterial | null {
        const newPath = [...pathStack, node.id];
        const children =
          node.nodes?.map(node => transformNode(node, newPath)).filter((n): n is Node => n !== null) || [];

        const hasMaterials = Boolean(materialByNode[node.id]?.length);

        if (hasMaterials || children.length) {
          return {
            ...node,
            nodes: children,
            material: materialByNode[node.id] || [],
            path: pathStack,
          };
        }

        return null;
      }

      return toc.map(node => transformNode(node, [])).filter((n): n is NodeWithMaterial => n !== null);
    }

    async function getTocNodes() {
      if (!selectedSubModule) return;

      try {
        const [{ data: tocNodes }, { data: userMaterial }] = await Promise.all([
          fetchTocNodes(selectedSubModule.id, true, true),
          getUserMaterial(selectedSubModule.id, { includeNodeHierarchy: true }),
        ]);

        const userMaterialGroupedByNode = groupBy(userMaterial, 'node');
        const nodeTree = buildMaterialNodeTree(tocNodes, userMaterialGroupedByNode);
        const allNodesById = keyBy(flattenNodes(nodeTree), 'id');

        setNodesWithMaterial(nodeTree);
        setAllNodesWithMaterialById(allNodesById);
      } catch (e) {
        setNodesWithMaterial([]);
      } finally {
        setIsLoadingTocNodes(false);
      }
    }

    getTocNodes();
  }, [selectedSubModule, t]);

  return {
    modules,
    nodesWithMaterial,
    allNodesWithMaterialById,
    isLoading: isLoadingTocNodes || isLoadingUserModules,
    fetchSubModulesForModule,
    subModulesForModule,
  };
}
